import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import MainLayout from "@/layouts/MainLayout";
import { GameWithDetails } from "@/types/game";
import { ExternalLink } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { MaterialIcon } from "@/components/ui/material-icon";
import ReviewsCarousel from "@/components/ReviewsCarousel";
import FatherGeekReview from "@/components/FatherGeekReview";
import { VideoThumbnail } from "@/components/VideoThumbnail";
import BuyButton from "@/components/BuyButton";
import { getPrimaryGameImage } from "@/lib/gameImages";
import creditsBarBg from "@/assets/credits-bar-bg.jpg.asset.json";

import { ProductGallery } from "@/components/ProductGallery";

const GameDetail = () => {
  const { slug } = useParams<{ slug: string }>();

  const {
    data: game,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["game-detail", slug],
    queryFn: async () => {
      const { data: games, error: gamesError } = await supabase.from("games").select("*").eq("is_published", true);
      if (gamesError) throw gamesError;

      const game = games?.find(
        (g) =>
          g.title
            .toLowerCase()
            .replace(/\s+/g, "-")
            .replace(/[^a-z0-9-]/g, "") === slug,
      );

      if (!game) throw new Error("Game not found");

      const [sectionsRes, reviewsRes, locationsRes, creditsRes, galleryRes] = await Promise.all([
        supabase.from("game_sections").select("*").eq("game_id", game.id).order("sort_order"),
        supabase.from("game_reviews").select("*").eq("game_id", game.id).order("sort_order"),
        supabase.from("game_play_locations").select("*").eq("game_id", game.id).order("sort_order"),
        supabase.from("game_credits").select("*").eq("game_id", game.id).order("sort_order"),
        supabase.from("game_gallery_images").select("*").eq("game_id", game.id).order("sort_order"),
      ]);

      return {
        ...game,
        sections: sectionsRes.data || [],
        reviews: reviewsRes.data || [],
        play_locations: locationsRes.data || [],
        credits: creditsRes.data || [],
        gallery_images: galleryRes.data || [],
      } as GameWithDetails;
    },
  });

  if (isLoading) {
    return (
      <MainLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </MainLayout>
    );
  }

  if (error || !game) {
    return (
      <MainLayout>
        <div className="min-h-screen flex flex-col items-center justify-center gap-4">
          <h1 className="text-2xl font-bold">Game not found</h1>
          <Link to="/games" className="btn-outline-dark">
            Back to Games
          </Link>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Helmet>
        <title>{game.title} | Rogue Skull Games</title>
        <meta name="description" content={game.short_description || `Learn more about ${game.title}`} />
        <link rel="canonical" href={`https://rogueskullgames.com/games/${slug}`} />
        <meta property="og:title" content={`${game.title} | Rogue Skull Games`} />
        <meta property="og:description" content={game.short_description || `Learn more about ${game.title}`} />
        <meta property="og:url" content={`https://rogueskullgames.com/games/${slug}`} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: game.title,
            description: game.short_description || `Learn more about ${game.title}`,
            url: `https://rogueskullgames.com/games/${slug}`,
            ...((game as any).intro_image_url || (game as any).banner_image_url
              ? { image: (game as any).intro_image_url || (game as any).banner_image_url }
              : {}),
            brand: { "@type": "Brand", name: "Rogue Skull Games" },
            ...((game as any).gamecrafter_sku ? { sku: (game as any).gamecrafter_sku } : {}),
            ...(game.cost != null && game.cost > 0
              ? {
                  offers: {
                    "@type": "Offer",
                    price: game.cost.toFixed(2),
                    priceCurrency: "USD",
                    url: `https://rogueskullgames.com/games/${slug}`,
                    availability: (game as any).gamecrafter_sku
                      ? "https://schema.org/InStock"
                      : "https://schema.org/PreOrder",
                  },
                }
              : {}),
          })}
        </script>
      </Helmet>

      {/* Shop-Style Product Section */}
      <section className="bg-white pt-40 pb-16">
        <div className="container mx-auto">
          {/* Back link
          <div className="mb-6">
            <Link
              to="/games"
              className="inline-flex items-center gap-2 text-[hsl(0,0%,40%)] hover:text-[hsl(0,0%,10%)] transition-colors"
            >
              <MaterialIcon name="arrow_back" size="sm" />
              <span className="text-sm uppercase tracking-wider">Back to Games</span>
            </Link>
          </div> */}

          <div className="grid md:grid-cols-4 xl:grid-cols-5 gap-10 xl:gap-16">
            {/* Left: Product Gallery - 60% */}
            <div className="md:col-span-2 xl:col-span-3 min-w-0">
              <ProductGallery images={(game as any).gallery_images || []} title={game.title} />
            </div>

            {/* Right: Product Info - 40% */}
            <div className="md:col-span-2 xl:col-span-2 min-w-0 space-y-6">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  {game.coming_soon && (
                    <span className="inline-block px-3 py-1 bg-blood-red text-white text-xs uppercase tracking-[0.15em] font-semibold">
                      Coming Soon
                    </span>
                  )}
                  {game.in_development && !game.coming_soon && (
                    <span className="in-development-badge">In Development</span>
                  )}
                  {game.playtesting && <span className="playtesting-badge">Playtesting</span>}
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-[hsl(0,0%,10%)] leading-tight">
                  {game.title}
                </h1>
                {game.hero_tagline && (
                  <span className="block text-blood-red font-semibold tracking-[0.2em] mt-2 uppercase text-sm">
                    {game.hero_tagline}
                  </span>
                )}
              </div>

              {/* Price */}
              {game.cost != null && game.cost > 0 && (
                <p className="text-3xl md:text-4xl font-bold text-[hsl(0,0%,10%)]">${game.cost.toFixed(2)}</p>
              )}

              {/* Description */}
              {game.short_description && (
                <p className="text-[hsl(0,0%,35%)] text-base md:text-lg leading-relaxed">{game.short_description}</p>
              )}

              {/* Quick Stats */}
              {(game.number_of_players || game.timeframe || game.age || game.complexity) && (
                <div className="flex flex-wrap gap-x-6 gap-y-3 py-4 border-t border-b border-[hsl(0,0%,90%)]">
                  {game.number_of_players && (
                    <div className="flex items-center gap-2">
                      <MaterialIcon name="group" size="md" className="text-blood-red" />
                      <span className="text-[hsl(0,0%,25%)]">{game.number_of_players} Players</span>
                    </div>
                  )}
                  {game.timeframe && (
                    <div className="flex items-center gap-2">
                      <MaterialIcon name="schedule" size="md" className="text-blood-red" />
                      <span className="text-[hsl(0,0%,25%)]">{game.timeframe}</span>
                    </div>
                  )}
                  {game.age && (
                    <div className="flex items-center gap-2">
                      <MaterialIcon name="escalator_warning" size="md" className="text-blood-red" />
                      <span className="text-[hsl(0,0%,25%)]">Ages {game.age}</span>
                    </div>
                  )}
                  {game.complexity && (
                    <div className="flex items-center gap-2">
                      <MaterialIcon
                        name={
                          game.complexity === "light" ? "stat_0" : game.complexity === "medium" ? "layers" : "stacks"
                        }
                        size="md"
                        className="text-blood-red"
                      />
                      <span className="text-[hsl(0,0%,25%)] capitalize">{game.complexity} Complexity</span>
                    </div>
                  )}
                </div>
              )}

              {/* Add to Bag */}
              <BuyButton
                gameId={game.id}
                title={game.title}
                slug={slug || ""}
                sku={(game as any).gamecrafter_sku}
                shopUrl={game.shop_url}
                price={game.cost}
                imageUrl={getPrimaryGameImage(game)}
                className="inline-flex items-center justify-center gap-2 bg-blood-red text-white border border-blood-red hover:bg-transparent hover:text-[hsl(0,0%,10%)] hover:border-[hsl(0,0%,75%)] transition-all duration-300 px-8 py-4 uppercase tracking-[0.2em] text-sm font-semibold w-full sm:w-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section - LIGHT THEME */}
      {((game as any).intro_image_url ||
        (game as any).intro_text ||
        (game as any).unique_features ||
        (game as any).game_variants) && (
        <section className="bg-white pt-24 pb-32 relative overflow-hidden">
          <div className="container mx-auto relative z-10">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-32 items-start">
              {/* Left column: Image + About the Game text */}
              <div className="space-y-8">
                {(game as any).intro_image_url && (
                  <div className="relative group">
                    <div className="relative overflow-hidden bg-[hsl(0,0%,95%)]">
                      <img
                        src={(game as any).intro_image_url}
                        alt={`${game.title} game box`}
                        className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </div>
                    <div className="absolute -bottom-2 -right-2 w-16 h-16 border-b-2 border-r-2 border-blood-red/40 hidden lg:block" />
                    <div className="absolute -top-2 -left-2 w-8 h-8 border-t-2 border-l-2 border-[hsl(0,0%,80%)] hidden lg:block" />
                  </div>
                )}

                {((game as any).intro_title || (game as any).intro_text) && (
                  <div>
                    <div className="flex items-center gap-4">
                      <span className="section-label">About the Game</span>
                    </div>
                    {(game as any).intro_title && (
                      <h2 className="section-title text-white">{(game as any).intro_title}</h2>
                    )}
                    {(game as any).intro_text && (
                      <div className="relative mt-2 space-y-5">
                        {String((game as any).intro_text)
                          .split(/\r?\n+/)
                          .map((paragraph) => paragraph.trim())
                          .filter(Boolean)
                          .map((paragraph, index) => (
                            <p key={`${index}-${paragraph.slice(0, 24)}`} className="text-lg leading-relaxed">
                              {paragraph}
                            </p>
                          ))}
                      </div>
                    )}
                    {/* <div className="mt-10">
                      <BuyButton
                        gameId={game.id}
                        title={game.title}
                        slug={slug || ""}
                        sku={(game as any).gamecrafter_sku}
                        shopUrl={game.shop_url}
                        price={game.cost}
                        imageUrl={getPrimaryGameImage(game)}
                        className="inline-flex items-center justify-center gap-2 bg-blood-red text-white border border-blood-red hover:bg-transparent hover:text-[hsl(0,0%,10%)] hover:border-[hsl(0,0%,75%)] transition-all duration-300 px-7 py-3.5 uppercase tracking-[0.2em] text-xs font-semibold w-full sm:w-auto"
                      />
                    </div> */}
                  </div>
                )}
              </div>

              {/* Right column: What Makes It Different + Game Variants */}
              <div className="space-y-8 lg:py-8">
                {(game as any).unique_features && (game as any).unique_features.length > 0 && (
                  <div>
                    {/* <h3 className="text-2xl md:text-3xl font-display font-bold text-[hsl(0,0%,10%)] mb-6">
                      {(game as any).unique_features_title || "What Makes It Different"}
                    </h3> */}
                    <ul className="space-y-4">
                      {(game as any).unique_features.map((feature: { icon: string; text: string }, index: number) => (
                        <li key={index} className="flex items-center gap-3">
                          <MaterialIcon
                            name={feature.icon || "check_circle"}
                            size="lg"
                            className="text-blood-red flex-shrink-0"
                          />
                          <span className="text-[hsl(0,0%,25%)] text-lg">{feature.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(game as any).game_variants && (game as any).game_variants.length > 0 && (
                  <div className="overflow-hidden border border-[hsl(0,0%,85%)] p-6 lg:p-8 h-fit">
                    <h4 className="text-sm uppercase tracking-[0.2em] text-blood-red font-semibold mb-5">
                      Game Variants Included
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {(game as any).game_variants.map((variant: string, index: number) => (
                        <span
                          key={index}
                          className="overflow-hidden px-3 py-1.5 border border-[hsl(0,0%,85%)] text-[hsl(0,0%,30%)] text-sm"
                        >
                          {variant}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Banner Image Section */}
      {(game as any).banner_image_url && (
        <section className="bg-white py-24 md:py-32 relative overflow-hidden">
          <div className="container mx-auto ">
            <img
              src={(game as any).banner_image_url}
              alt={`${game.title} banner`}
              className="w-full h-auto object-cover"
            />
          </div>
        </section>
      )}

      {/* Videos Section - keep dark since it has bg image */}
      {(game.video_1_url && game.video_1_thumbnail_url) || (game.video_2_url && game.video_2_thumbnail_url) ? (
        <section className="relative overflow-hidden min-h-[600px]">
          {(game as any).video_section_bg_url && (
            <>
              <img
                src={(game as any).video_section_bg_url}
                alt="Video section background"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-dark/50" />
            </>
          )}
          {!(game as any).video_section_bg_url && <div className="absolute inset-0 section-dark" />}

          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[600px] h-[600px] bg-blood-red/5 rounded-full blur-[150px]" />
          </div>

          <div className="container mx-auto  py-16 md:py-24 lg:py-32 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div className="hidden lg:block" />
              <div className="space-y-8">
                <div className="mb-4">
                  <span className="section-label">Watch</span>
                  <h2 className="section-title">Overview</h2>
                </div>
                {game.video_1_url && game.video_1_thumbnail_url && (
                  <div className="relative group">
                    <div className="absolute -top-2 -left-2 w-8 h-8 border-t-2 border-l-2 border-blood-red/50 hidden md:block" />
                    <VideoThumbnail
                      thumbnailUrl={game.video_1_thumbnail_url}
                      videoUrl={game.video_1_url}
                      title={game.video_1_title || `${game.title} Video 1`}
                    />
                  </div>
                )}
                {game.video_2_url && game.video_2_thumbnail_url && (
                  <div className="relative group">
                    <div className="absolute -bottom-2 -right-2 w-8 h-8 border-b-2 border-r-2 border-dark-foreground/20 hidden md:block" />
                    <VideoThumbnail
                      thumbnailUrl={game.video_2_thumbnail_url}
                      videoUrl={game.video_2_url}
                      title={game.video_2_title || `${game.title} Video 2`}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* How to Play - LIGHT THEME */}
      {game.sections.length > 0 && (
        <section className="bg-[hsl(0,0%,96%)] py-24 md:py-32 relative overflow-hidden">
          <div className="container mx-auto  relative z-10">
            <div className="text-center mb-16 md:mb-20">
              <span className="section-label">Discover</span>
              <h2 className="section-title text-[hsl(0,0%,10%)]">How to Play</h2>
            </div>
            <div className="mx-auto space-y-24 md:space-y-32">
              {game.sections.map((section, index) => (
                <div
                  key={section.id}
                  className={`flex flex-col md:flex-row items-center gap-12 md:gap-16 lg:gap-20 ${index % 2 === 1 ? "md:flex-row-reverse" : ""}`}
                >
                  {section.image_url && (
                    <div className="w-full md:w-[55%] flex-shrink-0 group">
                      <div className="relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-blood-red/80 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 ease-out z-10" />
                        <img
                          src={section.image_url}
                          alt={section.header}
                          className="w-full h-auto min-h-[250px] md:min-h-[450px] object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                    </div>
                  )}
                  <div className="w-full md:w-[45%] space-y-6">
                    <span className="text-blood-red text-6xl font-display font-bold">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <h3 className="text-2xl md:text-3xl lg:text-4xl font-display font-bold uppercase tracking-wide text-white">
                      {section.header}
                    </h3>
                    {section.info && <p className="leading-relaxed text-base md:text-lg">{section.info}</p>}
                  </div>
                  {!section.image_url && <div className="w-full md:w-1/2" />}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Father Geek Professional Review - Order of the Blade only */}
      {slug === "order-of-the-blade" && <FatherGeekReview />}

      {/* Reviews Section - LIGHT THEME */}
      {game.reviews.length > 0 && (
        <section className="bg-[hsl(0,0%,96%)] py-24 md:py-32 relative overflow-hidden">
          <div className="container mx-auto ">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <span className="section-label">Testimonials</span>
                <h2 className="section-title text-[hsl(0,0%,10%)] mb-6">Reviews</h2>
                <p className="text-[hsl(0,0%,40%)] text-lg max-w-2xl mx-auto">
                  See what others are saying about this game. Want to submit your own review? Email us at{" "}
                  <a href="mailto:rogueskullgames@gmail.com" className="text-blood-red hover:underline">
                    rogueskullgames@gmail.com
                  </a>
                </p>
              </div>
              <ReviewsCarousel reviews={game.reviews} />
            </div>
          </div>
        </section>
      )}

      {/* Box Contents Section - LIGHT THEME */}
      {(game as any).box_contents_items && (game as any).box_contents_items.length > 0 && (
        <section className="bg-white py-24 md:py-32 relative overflow-hidden">
          <div className="container mx-auto  relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <span className="section-label">{(game as any).box_contents_title || "What's in the Box?"}</span>
                <h2 className="section-title text-[hsl(0,0%,10%)]">Components</h2>
              </div>

              {(game as any).box_contents_image_url && (
                <div className="relative group mb-12 max-w-4xl mx-auto">
                  <div className="absolute -bottom-3 -left-3 w-20 h-20 border-b-2 border-l-2 border-blood-red/40 hidden md:block" />
                  <div className="absolute -top-3 -right-3 w-12 h-12 border-t-2 border-r-2 border-[hsl(0,0%,80%)] hidden md:block" />
                  <div className="relative overflow-hidden">
                    <img
                      src={(game as any).box_contents_image_url}
                      alt="Box contents"
                      className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                </div>
              )}

              <ul className="grid md:grid-cols-2 gap-4">
                {((game as any).box_contents_items as string[]).map((item, index) => (
                  <li
                    key={index}
                    className="group flex items-center gap-4 p-4 border border-[hsl(0,0%,85%)] bg-[#0c0c0c] transition-all duration-300 hover:border-blood-red/30"
                  >
                    <div className="w-8 h-8 flex items-center justify-center bg-blood-red/10 text-blood-red shrink-0">
                      <span className="material-symbols-outlined text-xl">check</span>
                    </div>
                    <span className="text-[hsl(0,0%,15%)] text-lg">{item}</span>
                  </li>
                ))}
              </ul>

              {(game as any).rulebook_pdf_url && (
                <div className="mt-20 pt-20 border-t border-[hsl(0,0%,85%)]">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:items-center">
                    <div className="space-y-2 md:col-span-3">
                      {(game as any).rulebook_title && (
                        <h3 className="text-xl md:text-2xl font-display font-bold uppercase tracking-wide text-[hsl(0,0%,10%)]">
                          {(game as any).rulebook_title}
                        </h3>
                      )}
                      {(game as any).rulebook_description && (
                        <p className="text-[hsl(0,0%,35%)] whitespace-pre-line">{(game as any).rulebook_description}</p>
                      )}
                    </div>
                    <div className="md:col-span-2 inline-flex justify-end">
                      <a
                        href={(game as any).rulebook_pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center  gap-3 px-6 py-3 bg-blood-red text-white font-semibold uppercase tracking-wider text-sm transition-all duration-300 hover:bg-blood-red/90 hover:-translate-y-0.5 shrink-0 w-full md:w-auto"
                        onClick={() => {
                          supabase.from("rulebook_downloads").insert({ game_id: game.id }).then();
                        }}
                      >
                        <span className="material-symbols-outlined text-xl">download</span>
                        {(game as any).rulebook_button_text || "Download Rulebook"}
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Wanna Play & Credits Section - LIGHT THEME */}
      {(game.play_locations.length > 0 || game.credits.length > 0) && (
        <section
          className="py-24 md:py-32 relative overflow-hidden bg-cover bg-center"
          style={{ backgroundImage: `url(${creditsBarBg.url})` }}
        >
          {/* Dark scrim so the boxes stay readable over the photo */}
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(135deg, hsl(0 0% 4% / 0.55) 0%, hsl(0 0% 0% / 0.72) 100%)" }}
          />
          <div className="container mx-auto relative">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 lg:gap-20">
                {game.credits.length > 0 && (
                  <div className="group relative p-8 md:p-10 border border-[hsl(0,0%,85%)] bg-black/65 backdrop-blur-md transition-all duration-500 hover:-translate-y-1">
                    <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-blood-red/80 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 ease-out" />
                    <span className="section-label">The Team</span>
                    <h3 className="text-xl md:text-2xl font-display font-bold uppercase tracking-wide text-[hsl(0,0%,10%)] mb-6">
                      {game.credits_title || "Credits"}
                    </h3>
                    <ul className="space-y-2">
                      {game.credits.map((credit) => (
                        <li key={credit.id} className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
                          <span className="text-[hsl(0,0%,45%)] text-sm uppercase tracking-wider">
                            {credit.role_title}:
                          </span>
                          <span className="text-[hsl(0,0%,10%)] font-medium sm:pl-0">{credit.person_name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {game.play_locations.length > 0 && (
                  <div className="group relative p-8 md:p-10 border border-[hsl(0,0%,85%)] bg-black/65 backdrop-blur-md transition-all duration-500 hover:-translate-y-1">
                    <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-blood-red/80 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 ease-out" />
                    <span className="section-label">{game.wanna_play_title || "Wanna Play?"}</span>
                    <h3 className="text-xl md:text-2xl font-display font-bold uppercase tracking-wide text-[hsl(0,0%,10%)] mb-6">
                      Find this Game
                    </h3>
                    <ul className="space-y-2">
                      {game.play_locations.map((location) => (
                        <li key={location.id} className="flex items-center gap-3">
                          {location.location_url ? (
                            <a
                              href={location.location_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[hsl(0,0%,25%)] hover:text-blood-red transition-colors"
                            >
                              {location.location_name}
                            </a>
                          ) : (
                            <span className="text-[hsl(0,0%,40%)]">{location.location_name}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Call to Action - LIGHT THEME */}
      <section className="py-24 md:py-32 bg-white relative overflow-hidden">
        <div className="container mx-auto ">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="section-title text-[hsl(0,0%,10%)] mb-6">Ready to Play?</h2>
            <p className="text-[hsl(0,0%,40%)] text-lg mb-10 max-w-2xl mx-auto">
              Gather your crew and prepare for an unforgettable gaming experience. Every game is crafted to bring out
              the competitive spirit in you.
            </p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap justify-center gap-4">
              <BuyButton
                gameId={game.id}
                title={game.title}
                slug={slug || ""}
                sku={(game as any).gamecrafter_sku}
                shopUrl={game.shop_url}
                price={game.cost}
                imageUrl={getPrimaryGameImage(game)}
                className="inline-flex items-center justify-center gap-2 bg-blood-red text-white border border-blood-red hover:bg-transparent hover:text-[hsl(0,0%,10%)] hover:border-[hsl(0,0%,75%)] transition-all duration-300 px-7 py-3.5 uppercase tracking-[0.2em] text-xs font-semibold w-full sm:w-auto"
              />
              <Link
                to="/games"
                className="inline-flex items-center justify-center gap-2 border border-[hsl(0,0%,75%)] text-[hsl(0,0%,10%)] bg-transparent hover:border-blood-red hover:bg-blood-red hover:text-white transition-all duration-300 px-7 py-3.5 uppercase tracking-[0.2em] text-xs font-semibold w-full sm:w-auto text-center"
              >
                Explore More Games
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MainLayout>
  );
};

export default GameDetail;
